import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { TbAlertCircleFilled, TbInfoCircle, TbMapPinFilled } from 'react-icons/tb';

import { useFetchAvailableNearbyCitiesQuery } from '@/api/hooks/useRestaurant';
import { useFetchStripeCheckoutSessionRequest } from '@/api/hooks/useSubscription';
import { CityResponse } from '@/api/lib/city';
import { RestaurantResponse } from '@/api/lib/restaurant';
import { stripePublishableKey } from '@/configs/config';

import ipadMRH from '../../../assets/images/ad/ipadMRH.webp';
import iphoneMRH from '../../../assets/images/ad/iphoneMRH.webp';
import { AdInfos } from '../AdInfos';
import { BaseModal } from '../BaseModal';
import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';
import AutocompleteCity from '@/components/forms/custom/AutocompleteCity/AutocompleteCity';

export interface Props {
  restaurant?: RestaurantResponse;
}

export const HeaderBoostBlock = ({ restaurant }: Props) => {
  const [selectedCity, setSelectedCity] = useState<CityResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const [currentPartnerCity, setCurrentPartnerCity] = useState<{ partnerName?: string; partnerId?: string } | null>(null);
  const [displayedCitiesCount, setDisplayedCitiesCount] = useState(6);

  const { data: nearbyCities } = useFetchAvailableNearbyCitiesQuery(restaurant?.id || '');

  useEffect(() => {
    const mainCity = nearbyCities?.[0];
    if (!mainCity) {
      return;
    }
    if (mainCity.restaurantId) {
      setCurrentPartnerCity({ partnerName: mainCity.displayName, partnerId: mainCity.restaurantId });
    }
  }, [nearbyCities]);

  const stripePromise = loadStripe(stripePublishableKey);

  const { mutate } = useFetchStripeCheckoutSessionRequest({
    onSuccess: (data: any) => {
      if (data.clientSecret) {
        setClientSecret(data.clientSecret);
        setShowCheckoutModal(true);
      } else {
        console.error("Erreur: L'URL Stripe n'est pas disponible.");
      }
    }
  });

  const handleCitySelect = (city: CityResponse) => {
    setSelectedCity(city);
  };

  const handleCityBoost = (city: CityResponse) => {
    setSelectedCity(city);
    setLoading(true);

    mutate(
      {
        restaurantId: restaurant?.id || '',
        cityId: city.id
      },
      {
        onError: () => {
          toast.error("Une erreur s'est produite lors de la récupération de vos données Stripe", {
            position: 'bottom-right',
            duration: 5000
          });
          setLoading(false);
        },
        onSuccess: (data: any) => {
          if (data.clientSecret) {
            setClientSecret(data.clientSecret);
            setShowCheckoutModal(true);
          }
        },
        onSettled: () => {
          setLoading(false);
        }
      }
    );
  };

  const handlePartnerCity = (partner: { partnerName?: string; partnerId?: string }) => {
    setCurrentPartnerCity(partner);
  };

  const handleToggleModal = () => {
    setShowModal((prev) => !prev);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setShowCheckoutModal(false);
  };

  const loadMoreCities = () => {
    setDisplayedCitiesCount((prev) => prev + 6);
  };

  return (
    <>
      <div className="flex flex-col md:grid md:grid-cols-5 gap-5 p-4">
        <div className="flex order-1 md:order-3 md:col-span-3 h-full md:gap-5 flex-col md:flex-row">
          <div className="z-10 my-auto w-full md:p-5 p-4">
            <span className="flex items-center gap-2 h-full">
              <span>
                <Typography variant="heroTitle">57€/mois &</Typography>
                <Typography variant="heroTitle" weight="bold">
                  sans engagement
                </Typography>
              </span>
              <img
                alt="rocket"
                draggable={false}
                className="w-12 h-12 md:w-24 md:h-24 object-contain"
                src="https://media.tenor.com/GocCvG7hs78AAAAi/rocket-joypixels.gif"
              />
            </span>
            <Typography variant="h2" weight="medium">
              Je veux booster la visibilité de <strong>{restaurant?.name || '--'}</strong>
            </Typography>

            {loading ? (
              <div className="flex gap-2 items-center p-2 md:p-5 mt-5 border rounded-lg bg-emerald-400">
                <Spinner />
                <Typography variant="h3" color="white">
                  Chargement de la page de paiement pour la ville de <strong>{selectedCity?.name}</strong>
                </Typography>
              </div>
            ) : (
              <>
                <Typography variant="paragraph" color="gray" marginClassName="mb-2 mt-5">
                  Ville de diffusion de votre publicité :
                </Typography>
                <AutocompleteCity
                  defaultCity={restaurant?.cityName || '--'}
                  onCitySelect={handleCitySelect}
                  onPartnerCityFound={handlePartnerCity}
                  isOnError={!!currentPartnerCity?.partnerName && !!selectedCity}
                />

                {!currentPartnerCity?.partnerName ? (
                  <button
                    disabled={!selectedCity || loading}
                    onClick={() => {
                      if (selectedCity) {
                        handleCityBoost({ id: selectedCity.id, name: selectedCity.name } as CityResponse);
                      }
                    }}
                    className=" disabled:cursor-not-allowed rounded-full px-5 py-1.5 mt-4 w-full duration-300 ease-in-out bg-primary hover:bg-shades-primary-hover"
                  >
                    <Typography variant="h3" color="white" align="center">
                      Acheter le boost
                    </Typography>
                  </button>
                ) : (
                  <>
                    <div className="flex gap-1 items-center mt-1 ml-2">
                      <TbAlertCircleFilled className="text-red-600 text-sm" />
                      <Typography variant="extraSmall" color="failure">
                        {currentPartnerCity.partnerId === restaurant?.id
                          ? 'Cette ville est déjà réservée par votre restaurant !'
                          : 'Cette ville est déjà réservée par un autre restaurant !'}
                      </Typography>
                    </div>

                    {nearbyCities && nearbyCities.length > 1 ? (
                      <div className="mt-4 border p-3 md:p-5 rounded-xl bg-gray-100 border-gray-300">
                        <Typography variant="h3" color="black" marginClassName="mb-5">
                          Villes à proximité de votre restaurant :
                        </Typography>
                        <div className="grid md:grid-cols-3 gap-3">
                          {nearbyCities.slice(0, displayedCitiesCount).map((city) => (
                            <button
                              key={city.cityId}
                              onClick={() => handleCityBoost({ id: city.cityId, name: city.displayName } as CityResponse)}
                              disabled={loading || !!city.restaurantId}
                              className={`disabled:cursor-not-allowed flex flex-col rounded-xl px-4 py-2 duration-300 ease-in-out ${
                                !city.restaurantId
                                  ? 'bg-emerald-500 hover:bg-emerald-800 outline hover:outline-emerald-800 outline-emerald-500'
                                  : 'bg-red-500 hover:bg-red-800 outline hover:outline-red-800 outline-red-500'
                              } border-2 border-white text-white`}
                            >
                              <span className="flex gap-1 items-center justify-center">
                                <TbMapPinFilled className="text-white" />
                                <span>
                                  <p className="text-sm">{city.displayName}</p>
                                  <p className="text-sm">{!city.restaurantId ? '(Disponible)' : '(Indisponible)'}</p>
                                </span>
                              </span>
                            </button>
                          ))}
                        </div>
                        {nearbyCities.length > displayedCitiesCount && (
                          <div className="flex justify-center mt-4">
                            <button
                              onClick={loadMoreCities}
                              className="text-black hover:text-black hover:underline hover:decoration-black transition-colors duration-300 flex items-center gap-1"
                            >
                              <Typography variant="paragraph">Afficher plus de villes</Typography>
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <Spinner />
                    )}
                  </>
                )}
              </>
            )}

            <button onClick={handleToggleModal} className="flex mt-5 text-start items-center gap-1.5 hover:underline underline-offset-2">
              <TbInfoCircle className="text-black text-xl" />
              <Typography variant="h4" color="black">
                En savoir plus sur les publicités
              </Typography>
            </button>
          </div>
        </div>

        <div className="flex order-2 md:order-1 flex-col gap-3 md:gap-5 pt-8 w-full bg-primary rounded-xl h-full p-2">
          <Typography tag="p" variant="h3" weight="semibold" align="center" color="white">
            Première place des recherches dans ma ville
          </Typography>
          <img draggable={false} alt={iphoneMRH} width={400} height={200} src={ipadMRH} className="h-full object-contain" />
        </div>

        <div className="flex order-3 md:order-2 flex-col gap-3 md:gap-5 pt-8 w-full bg-primary rounded-xl h-full p-2">
          <Typography tag="p" variant="h3" weight="semibold" align="center" color="white">
            Une pop-up sur la fiche de vos concurrents
          </Typography>
          <img draggable={false} alt={iphoneMRH} width={400} height={200} src={iphoneMRH} className="h-full object-contain" />
        </div>
      </div>

      <BaseModal maxWidth="xxl" isOpen={showModal} onCloseClick={handleModalClose}>
        <AdInfos />
      </BaseModal>
      {clientSecret && showCheckoutModal && (
        <BaseModal maxWidth="xxl" isOpen={showCheckoutModal} onCloseClick={handleModalClose}>
          <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </BaseModal>
      )}
    </>
  );
};
